import { useEffect, useState } from "react";
import { formatDate } from "../../../../api/DateUtils";
import { Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Column } from "react-table";
import EpaCard from "../../../../leap-components/EpaCard";
import BreadCrumbs from "./BreadCrumbs";
import Header from "./Header";
import { Grid } from "../../../../leap-components/Grid";
import AbstractionHomeContainer from "./AbstractionContainer";

import {
  GetAbstractionsFetchUrl,
  GetFilteredAbstractionsFetchUrl,
  GetFilteredAbstractionsCsv,
  GetAbstractionPointsCsvUrl,
} from "../../../../api/Abstractions/IAbstractions";

import { EpaGridOrderByFetchParams } from "../../../../leap-components/Grid/EpaGridProps";
import { CreateStaticSelectColumnFilter } from "../../../../leap-components/Grid/FetchGrid/StaticSelectColumnFilter";

interface ISearchResultRow {
  registernumber: string;
  organisationname: string;
  sitecomputedaddress: string;
  county: string;
  abstractionpurpose: string;
}

const GetDetailsUrl = (regNo: string) => {
  return `/abstractions/${regNo}`;
};

const AbstractionList = () => {
  const [searchValue, setSearchValue] = useState("");

  const searchChangedFn = (newSearch: string) => {
    setSearchValue(newSearch);
  };

  const countyFilterValues = [
    { value: "Antrim", label: "Antrim" },
    { value: "Armagh", label: "Armagh" },
    { value: "Carlow", label: "Carlow" },
    { value: "Cavan", label: "Cavan" },
    { value: "Clare", label: "Clare" },
    { value: "Cork", label: "Cork" },
    { value: "Derry", label: "Derry" },
    { value: "Donegal", label: "Donegal" },
    { value: "Down", label: "Down" },
    { value: "Dublin", label: "Dublin" },
    { value: "Fermanagh", label: "Fermanagh" },
    { value: "Galway", label: "Galway" },
    { value: "Kerry", label: "Kerry" },
    { value: "Kildare", label: "Kildare" },
    { value: "Kilkenny", label: "Kilkenny" },
    { value: "Laois", label: "Laois" },
    { value: "Leitrim", label: "Leitrim" },
    { value: "Limerick", label: "Limerick" },
    { value: "Longford", label: "Longford" },
    { value: "Louth", label: "Louth" },
    { value: "Mayo", label: "Mayo" },
    { value: "Meath", label: "Meath" },
    { value: "Monaghan", label: "Monaghan" },
    { value: "Offaly", label: "Offaly" },
    { value: "Roscommon", label: "Roscommon" },
    { value: "Sligo", label: "Sligo" },
    { value: "Tipperary", label: "Tipperary" },
    { value: "Tyrone", label: "Tyrone" },
    { value: "Waterford", label: "Waterford" },
    { value: "Westmeath", label: "Westmeath" },
    { value: "Wexford", label: "Wexford" },
    { value: "Wicklow", label: "Wicklow" },
  ];

  //Applied, Registered, Licensed, Replaced, Withdrawn, Refused, Surrendered, Ceased (not commenced).

  const statusFilterValues = [
    { value: "Applied", label: "Applied" },
    { value: "Ceased (not commenced)", label: "Ceased (not commenced)" },
    { value: "Closed", label: "Closed" },
    { value: "Licensed", label: "Licensed" },
    { value: "Refused", label: "Refused" },
    { value: "Registered", label: "Registered" },
    { value: "Replaced", label: "Replaced" },
    { value: "Surrendered", label: "Surrendered" },
    { value: "Withdrawn", label: "Withdrawn" },
  ];

  const tableColumnsBig: Column<Record<string, ISearchResultRow>>[] = [
    {
      Header: "Register Number",
      accessor: "registernumber",
      Filter: false,
      Cell: (rowInfo: any) => {
        const rowData = rowInfo.data[rowInfo.row.index];
        return (
          <Link to={GetDetailsUrl(rowData.registernumber)}>
            {rowInfo.value}
          </Link>
        );
      },
      sortType: "basic",
    },
    {
      Header: "Organisation",
      accessor: "organisationname",
      Filter: false,
    },
    {
      Header: "Site",
      accessor: "sitecomputedaddress",
      Filter: false,
    },
    {
      Header: "County",
      accessor: "county",
      Filter: CreateStaticSelectColumnFilter(
        countyFilterValues.map((n) => n.value)
      ),
      filter: "includes",
    },
    {
      Header: "Abstraction Purpose",
      accessor: "abstractionpurpose",
      Filter: false,
    },
    {
      Header: "Maximum Daily Volume",
      accessor: "maximumdailyvolume",
      Filter: false,
    },
    {
      Header: "Authorisation Status",
      accessor: "licencestatus",
      Filter: CreateStaticSelectColumnFilter(
        statusFilterValues.map((n) => n.value)
      ),
      filter: "includes",
    },
    {
      Header: "Open For Submissions",
      accessor: "openforsubmissiontext",
      Filter: false,
      sortType: "basic",
    },
  ];

  const tableColumnsSmall: Column<Record<string, ISearchResultRow>>[] = [
    {
      Header: "Register Number",
      accessor: "registernumber",
      Filter: false,
      Cell: (rowInfo: any) => {
        const rowData = rowInfo.data[rowInfo.row.index];
        return (
          <Link to={GetDetailsUrl(rowData.registernumber)}>
            {rowInfo.value}
          </Link>
        );
      },
      sortType: "basic",
    },
    {
      Header: "Organisation",
      accessor: "organisationname",
      Filter: false,
    },
    {
      Header: "Site",
      accessor: "sitecomputedaddress",
      Filter: false,
    },
    {
      Header: "County",
      accessor: "county",
      Filter: CreateStaticSelectColumnFilter(
        countyFilterValues.map((n) => n.value)
      ),
      filter: "includes",
    },
    {
      Header: "Abstraction Purpose",
      accessor: "abstractionpurpose",
      Filter: false,
    },
    {
      Header: "Maximum Daily Volume",
      accessor: "maximumdailyvolume",
      Filter: false,
    },
    {
      Header: "Authorisation Status",
      accessor: "licencestatus",
      Filter: CreateStaticSelectColumnFilter(
        statusFilterValues.map((n) => n.value)
      ),
      filter: "includes",
    },
    {
      Header: "Open For Submissions",
      accessor: "openforsubmissiontext",
      Filter: false,
      sortType: "basic",
    },
  ];

  const getFetchUrlFactory = () => {
    let fetchUrlFn = (
      pageNo: number,
      recordsPerPage: number,
      searchText: string,
      filters: any,
      orderBy?: EpaGridOrderByFetchParams
    ) => {
      return GetFilteredAbstractionsFetchUrl(
        pageNo,
        recordsPerPage,
        searchText,
        filters,
        orderBy
      );
    };

    return fetchUrlFn;
  };

  const fetchUrl = getFetchUrlFactory();

  const orderBy = {
    orderByAscending: false,
    orderByName: "Register Number",
    enabledOrderByColumns: ["Register Number", "Open For Submissions"],
  };

  const results = (
    <div>
      <div className="d-md-none">
        <Grid
          columns={tableColumnsSmall}
          data={[]}
          fetchUrlFunction={fetchUrl}
          enablePagination={true}
          enableQuickFind={true}
          orderBy={orderBy}
          exportToCsvFunction={GetFilteredAbstractionsCsv}
        />
      </div>
      <div className="d-none d-md-block">
        <Grid
          columns={tableColumnsBig}
          data={[]}
          fetchUrlFunction={fetchUrl}
          enablePagination={true}
          enableQuickFind={true}
          orderBy={orderBy}
          exportToCsvFunction={GetFilteredAbstractionsCsv}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <a
          href={GetAbstractionPointsCsvUrl()}
          target="_blank"
          rel="Download Abstraction Points"
          className="btn btn-primary"
          color="#FFFFFF"
        >
          <button>Download Abstraction Points CSV</button>
        </a>
      </div>
    </div>
  );

  const contentElement = (
    <div>
      <Row>
        <Col className="ps-0">
          <EpaCard title="Search Abstractions" content={results} />
        </Col>
      </Row>
    </div>
  );

  return (
    <div>
      <BreadCrumbs />
      <div className="ps-0">
        <Header />
        <AbstractionHomeContainer content={contentElement} />
      </div>
    </div>
  );
};

export default AbstractionList;
